import {
  FLUSH,
  PAUSE,
  PERSIST,
  PersistConfig,
  persistReducer,
  persistStore,
  PURGE,
  REGISTER,
  REHYDRATE,
} from "redux-persist";
import storage from "redux-persist/lib/storage";
import createFilter from "redux-persist-transform-filter";

import {
  combineReducers,
  configureStore,
  createListenerMiddleware,
  ThunkDispatch,
  UnknownAction,
} from "@reduxjs/toolkit";
import * as Sentry from "@sentry/react";
import { dashboardFiltersUrlSyncMiddleware } from "@src/store/middleware/dashboardFiltersUrlSync";
import { sitewideDashboardFiltersUrlSyncMiddleware } from "@src/store/middleware/sitewideDashboardFiltersUrlSync";
import { agProductsReducer } from "@src/store/slices/agProducts";
import { setupAgProductsListener } from "@src/store/slices/agProducts/setupAgProductsListener";
import { aiChatReducer, setAiChatListener } from "@src/store/slices/aiChat";
import { setupEllieChatMixPanelListener } from "@src/store/slices/aiChat/listeners/setupEllieChatMixPanelListener";
import { analyticsProductsReducer } from "@src/store/slices/analyticsProducts";
import { localSettingsReducer } from "@src/store/slices/appSettings";
import { audienceReducer } from "@src/store/slices/audiences";
import { setupAudiencesListener } from "@src/store/slices/audiences/setupAudiencesListener";
import { cartTargetingReducer } from "@src/store/slices/cartTargeting";
import { checkoutBlocksReducer } from "@src/store/slices/checkoutBlocks";
import { componentSlice } from "@src/store/slices/experienceBuilder/component/slice";
import {
  groupSliderReducer,
  setGroupSliderPercentages,
} from "@src/store/slices/components/groupSlider";
import { tabReducer } from "@src/store/slices/components/tab";
import { customEventsReducer } from "@src/store/slices/customEvents";
import { dashboardFiltersReducer } from "@src/store/slices/dashboardFilters";
import { draftExperienceReducer } from "@src/store/slices/draftExperience";
import { drawersReducer } from "@src/store/slices/drawers";
import { exclusionGroupsReducer } from "@src/store/slices/exclusionGroups";
import { experienceBuilderAiSlice } from "@src/store/slices/experienceBuilder/ai/slice";
import { basicModSlice } from "@src/store/slices/experienceBuilder/basicMod/slice";
import { ideasSlice } from "@src/store/slices/experienceBuilder/ideas/slice";
import { setupExperienceBuilderListener } from "@src/store/slices/experienceBuilder/setupListeners";
import { experienceBuilderSlice } from "@src/store/slices/experienceBuilder/slice";
import { experienceInfoStateReducer } from "@src/store/slices/experienceInfoState";
import { experienceMetricsReducer } from "@src/store/slices/experienceMetrics/experienceMetrics";
import { experienceProductIssuesReducer } from "@src/store/slices/experienceProductIssues";
import { setupExperienceProductIssuesListener } from "@src/store/slices/experienceProductIssues/setupExperienceProductIssuesListener";
import { experienceProductsReducer } from "@src/store/slices/experienceProducts";
import { experienceProductTargetingReducer } from "@src/store/slices/experienceProductTargeting";
import { experienceReducer } from "@src/store/slices/experiences";
import { setupCheckoutBlocksListener } from "@src/store/slices/checkoutBlocks/setupCheckoutBlocksListener";
import { setupExperiencesListener } from "@src/store/slices/experiences/setupExperiencesListener";
import { globalReducer } from "@src/store/slices/global";
import { setupMixPanelListener } from "@src/store/slices/mixpanel/setupMixPanelListener";
import { setupNavigationListener } from "@src/store/slices/navigation/setupNavigationListener";
import { offerProductsReducer } from "@src/store/slices/offerProducts";
import { experienceComponentsReducer } from "@src/store/slices/experienceComponents";
import { offersReducer } from "@src/store/slices/offers";
import { onsiteEditsReducer } from "@src/store/slices/onsiteEdits";
import { setupOnsiteEditsListener } from "@src/store/slices/onsiteEdits/setupOnsiteEditsListener";
import { onsiteInjectionsReducer } from "@src/store/slices/onsiteInjections";
import { organizationReducer } from "@src/store/slices/organization";
import { userReducer } from "@src/store/slices/user";
import { pageTargetingReducer } from "@src/store/slices/pageTargeting";
import { processesReducer } from "@src/store/slices/processes";
import { productPickerReducer } from "@src/store/slices/productPicker";
import { productsReducer } from "@src/store/slices/products";
import { redirectsReducer } from "@src/store/slices/redirects";
import { sitewideDashboardFiltersReducer } from "@src/store/slices/sitewideDashboardFilters";
import { themesReducer } from "@src/store/slices/themes";
import { upsellProductRulesReducer } from "@src/store/slices/upsellProductRules";
import { userInterfacesReducer } from "@src/store/slices/userInterfaces";
import { setupUserInterfaceListener } from "@src/store/slices/userInterfaces/setupUserInterfaceListener";
import { variationsReducer } from "@src/store/slices/variations";
import { widgetsReducer } from "@src/store/slices/widgets";
import { workflowInitialContextReducer } from "@src/store/slices/workflowInitialContext";

// Copied from node_modules/@sentry/react/dist/redux.d.ts
// Sentry does not export these interfaces for some reason
interface Action<T = never> {
  type: T;
}

interface AnyAction extends Action {
  [extraProps: string]: any;
}

const appSettingsFilter = createFilter("appSettings", [
  "isShopLogin",
  "snoozeShopifyScopesDialog",
  "loggedOrgs",
  "dismissedAlerts",
  "dismissedRecommendationCopies",
  "agenticLogicRuleIdeasByOrgAndContext",
  "agenticLogicRulePlaceholderByOrgAndContext",
  "rememberEmail",
  "onboardingPage",
  "showDuplicateProductIntro",
  "performanceViews",
  "showIds",
  "useWideApp",
  "sideNavCollapsed",
  "viewAsMode",
  "selectedViewAsProductId",
  "viewAsProductId",
  "prefersLegacyEditor",
]);

const persistConfig: PersistConfig<any> = {
  key: "root",
  storage,
  whitelist: ["appSettings", "workflowInitialContext"],
  transforms: [appSettingsFilter],
};

export const rootReducer = combineReducers({
  agProducts: agProductsReducer,
  aiChat: aiChatReducer,
  analyticsProducts: analyticsProductsReducer,
  appSettings: localSettingsReducer,
  audience: audienceReducer,
  cartTargeting: cartTargetingReducer,
  checkoutBlocks: checkoutBlocksReducer,
  customEvents: customEventsReducer,
  dashboardFilters: dashboardFiltersReducer,
  draftExperience: draftExperienceReducer,
  drawers: drawersReducer,
  exclusionGroups: exclusionGroupsReducer,
  experience: experienceReducer,
  experienceBuilder: experienceBuilderSlice.reducer,
  experienceBuilderAi: experienceBuilderAiSlice.reducer,
  experienceBuilderBasicMod: basicModSlice.reducer,
  experienceBuilderComponent: componentSlice.reducer,
  experienceBuilderIdeas: ideasSlice.reducer,
  experienceComponents: experienceComponentsReducer,
  experienceInfoState: experienceInfoStateReducer,
  experienceMetrics: experienceMetricsReducer,
  experienceProductIssues: experienceProductIssuesReducer,
  experienceProducts: experienceProductsReducer,
  experienceProductTargeting: experienceProductTargetingReducer,
  global: globalReducer,
  groupSlider: groupSliderReducer,
  offerProducts: offerProductsReducer,
  offers: offersReducer,
  onsiteEdits: onsiteEditsReducer,
  onsiteInjections: onsiteInjectionsReducer,
  organization: organizationReducer,
  user: userReducer,
  pageTargeting: pageTargetingReducer,
  processes: processesReducer,
  productPicker: productPickerReducer,
  products: productsReducer,
  redirects: redirectsReducer,
  sitewideDashboardFilters: sitewideDashboardFiltersReducer,
  tab: tabReducer,
  themes: themesReducer,
  upsellProductRules: upsellProductRulesReducer,
  userInterfaces: userInterfacesReducer,
  variations: variationsReducer,
  widgets: widgetsReducer,
  workflowInitialContext: workflowInitialContextReducer,
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- redux-persist types incompatible with RTK 2.x
const persistedReducer = persistReducer(persistConfig, rootReducer as any);

const sentryReduxEnhancer = Sentry.createReduxEnhancer({
  actionTransformer(action: AnyAction): AnyAction | null {
    const ignoredTypes: string[] = [];
    if (ignoredTypes.includes(action.type)) {
      return null;
    }

    if (action.token) {
      return { ...action, token: null };
    }
    return action;
  },
  stateTransformer(state: any): any {
    return state;
  },
});

type StoreState = ReturnType<typeof rootReducer>;
type StoreDispatch = ThunkDispatch<StoreState, undefined, UnknownAction>;

export const listenerMiddleware = createListenerMiddleware<
  StoreState,
  StoreDispatch
>();

setupMixPanelListener(listenerMiddleware);
setupAudiencesListener(listenerMiddleware);
setupExperiencesListener(listenerMiddleware);
setupCheckoutBlocksListener(listenerMiddleware);
setupUserInterfaceListener(listenerMiddleware);
setupAgProductsListener(listenerMiddleware);
setupOnsiteEditsListener(listenerMiddleware);
setAiChatListener(listenerMiddleware);
setupEllieChatMixPanelListener(listenerMiddleware);
setupExperienceProductIssuesListener(listenerMiddleware);
setupExperienceBuilderListener(listenerMiddleware);
setupNavigationListener(listenerMiddleware);

export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [
          FLUSH,
          REHYDRATE,
          PAUSE,
          PERSIST,
          PURGE,
          REGISTER,
          "global/openDialog",
        ],
        ignoredPaths: ["global.dialogs"],
      },
    })
      .prepend(listenerMiddleware.middleware)
      .concat(dashboardFiltersUrlSyncMiddleware)
      .concat(sitewideDashboardFiltersUrlSyncMiddleware),
  devTools: {
    actionsDenylist: [setGroupSliderPercentages.type],
  },
  enhancers: (getDefaultEnhancers) =>
    getDefaultEnhancers().concat([sentryReduxEnhancer]),
});

export const persistor = persistStore(store);
